import dayjs from 'dayjs';
import { createStore, del, entries, get, keys, update } from 'idb-keyval';
import { nanoid } from 'nanoid';

import { API_URL, CACHE_API_URL } from '@webapp/common/conf';
import { ApiRespCode, getFingerprint } from '@webapp/common/lib/api';
import { isBrowser, isPreview } from '@webapp/common/lib/const';
import { getCookie, setCookieWithSubdomains, setSurveyTokenCookie } from '@webapp/common/lib/cookies';
import { hasPendingRequests, ttFetch } from '@webapp/common/lib/fetch';
import { mapNested } from '@webapp/common/lib/map';
import { sleep } from '@webapp/common/lib/sleep';
import { LocalStorage, SessionStorage } from '@webapp/common/lib/storage';
import { getUrlSearchParam } from '@webapp/common/lib/utils';
import type {
    MaxDiffBlockModel,
    PageModel,
    QuestionLogicModel,
    QuestionModel,
    ResidenceModel,
    SurveyStore,
} from '@webapp/common/resources/entities';
import type { InfoModel } from '@webapp/common/resources/entities/info';
import type { NestedItem } from '@webapp/common/resources/question';
import { LogicType } from '@webapp/common/resources/survey';

import type { AnswersRequest } from '@webapp/survey/src/api/queries-api';
import { mapQuestion } from '@webapp/survey/src/utils/map';

import { loadSavedAnswers, prepareResidences } from './helpers/common';
import {
    mapInfoShorted,
    mapLinkParamsLogic,
    mapMaxDiffBlock,
    mapPages,
    mapQuestionLogic,
    mapQuestions,
} from './helpers/normalize';
import { isOfflineMode } from './is-offline-mode';
import { tryToGetTtId } from './tt-id';

const fetch = (config: AnyObject) =>
    ttFetch({
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
        },
        ...config,
    });

const fetchGet = ttFetch({
    method: 'GET',
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
    },
    retries: 1,
});

const fetchDel = ttFetch({
    method: 'DELETE',
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
    },
    retries: 1,
});

class SurveyRequest {
    static token: string = null;

    static post = async (path: string, body?: JsonValue | FormData, headers = {}, config = {}): Promise<any> => {
        // @ts-ignore
        const resp: AnyObject = await fetch(config)(config?.absPath ? path : `${API_URL}/survey/v1/${path}`, {
            headers: {
                ...(SurveyRequest.token && { 'X-Authorization': SurveyRequest.token }),

                ...headers,
            },
            // @ts-ignore
            body,
        });

        // if (resp.code !== ApiRespCode.OK_CODE) {
        if (resp.status !== 200) {
            throw resp;
        }

        return resp;
    };

    static get = (path: string, config: Record<any, any> = {}): Promise<any> =>
        fetchGet(config?.absPath ? path : `${API_URL}/survey/v1/${path}`, {
            headers: {
                ...(SurveyRequest.token && { 'X-Authorization': SurveyRequest.token }),
                ...(config?.headers || {}),
            },
        });

    static del = (path: string) =>
        fetchDel(`${API_URL}/survey/v1/${path}`, {
            headers: {
                ...(SurveyRequest.token && { 'X-Authorization': SurveyRequest.token }),
            },
        });
}

class CacheRequest {
    static token: string = null;

    static post = async (path: string, body?: JsonValue | FormData, headers = {}, config = {}): Promise<any> => {
        // @ts-ignore
        const resp: AnyObject = await fetch(config)(`${CACHE_API_URL}/v1/${path}`, {
            headers: {
                ...(SurveyRequest.token && { 'X-Authorization': SurveyRequest.token }),

                ...headers,
            },
            // @ts-ignore
            body,
        });

        // if (resp.code !== ApiRespCode.OK_CODE) {
        if (resp.status !== 200) {
            throw resp;
        }

        return resp;
    };

    static get = (path: string, headers = {}): Promise<any> =>
        fetchGet(`${CACHE_API_URL}/v1/${path}`, {
            headers: {
                ...(SurveyRequest.token && { 'X-Authorization': SurveyRequest.token }),

                ...headers,
            },
        });

    static del = (path: string) =>
        fetchDel(`${CACHE_API_URL}/v1/${path}`, {
            headers: {
                ...(SurveyRequest.token && { 'X-Authorization': SurveyRequest.token }),
            },
        });
}

const uploadFile = async (surveyId: string | number, file: File): Promise<AnyObject> => {
    const body = new FormData();

    body.append('upfile[]', file, file.name);
    body.append('question', String(surveyId));

    const { code, files, message } = await SurveyRequest.post('upload/response/file', body, {
        'Access-Control-Request-Headers': 'Content-Type,X-Authorization',
        'Access-Control-Request-Method': 'POST',
    });

    if (code !== ApiRespCode.OK_CODE) {
        const err = new Error(message);
        console.error(err);
        throw err;
    }

    const { id, path, realName } = files[0];

    return {
        id: parseInt(id),
        type: null,
        file: {
            id: parseInt(id),
            path,
            name: realName,
            size: file.size,
        },
    };
};

let nextSendIncomplete: number | null = null;
let lastTimeout: number | null = null;
let lastData: { token: string; answers: Array<unknown> } | null = null;

const SEND_INCOMPLETE_INTERVAL = 3 * 1_000;

export const hasPendingIncompleteSurveyRequests = () => {
    return !!nextSendIncomplete;
};

export const getSurveyLang = () =>
    new URLSearchParams(window.location.search).get('lang') || LocalStorage.get<string>('lang');

export const sendLastIncompleteImmediately = async () => {
    try {
        const ttId = tryToGetTtId();
        if (lastData && ttId) {
            await CacheRequest.post('cache', {
                data: JSON.stringify(lastData),
                key: ttId,
            });
        }
    } catch (e) {
        // Ошибка запроса не должна «залипнуть»: иначе флаги не сбросятся,
        // и beforeunload-диалог будет показываться при каждом закрытии вкладки.
        console.error(e);
    } finally {
        if (lastTimeout) {
            window.clearTimeout(lastTimeout);
        }
        lastTimeout = null;
        nextSendIncomplete = null;
        lastData = null;
    }
};

export type QuotaClaim = {
    questionId: number;
    answerIds: Array<number>;
};

export interface QuotaClaimResult {
    allowed: boolean;
    reason?: string;
    denied?: {
        questionId: number;
        logicId: number;
        action: number;
    } | null;
}

export class SurveyAPI {
    nestedControl: Record<number, boolean> = {};

    checkCanary = async (rewrite: string) => {
        const { isCanary } = await SurveyRequest.post('check/canary', {
            rewrite,
        });
        LocalStorage.set('survey_is_canary', isCanary, 60 * 60 * 24);
        if (isCanary) {
            setCookieWithSubdomains('is_canary', 'always', {
                expires: dayjs().add(1, 'day').toDate(),
            });
            // На localhost кука с Domain может не сохраниться — не уходим в бесконечный reload
            if (getCookie('is_canary')) {
                window.location.reload();
                await sleep(3000);
            }
        }
    };

    checkIndividualPassword = async (password: string): Promise<{ code: number; token: string }> => {
        const { token, code } = await SurveyRequest.post('check/individual_password', {
            password,
        });

        return { code, token };
    };

    checkPassword = async (password: string): Promise<{ code: number; token: string }> => {
        const { token, code } = await SurveyRequest.post('check/password', {
            password,
        });

        return { code, token };
    };

    checkVkAuth = async (rewrite: string, vkauth: string, currToken?: string, referer?: string): Promise<any> => {
        const { token } = await SurveyRequest.post('check/vk_auth', {
            rewrite,
            token: currToken,
            vkauth,
            referer,
        });

        return token;
    };

    // Бронирование квот вопросов. claims — все квотные вопросы текущей
    // страницы с выбранными под них ответами; сервис квот сам решает,
    // влезаем ли мы, и атомарно переставляет прежние брони этих вопросов.
    claimQuotaAnswers = async (claims: Array<QuotaClaim>): Promise<QuotaClaimResult> => {
        const resp = await SurveyRequest.post('claim/quota_answers', { claims });

        return {
            allowed: Boolean(resp.allowed),
            reason: resp.reason,
            denied: resp.denied ?? null,
        };
    };

    confirmAuthCode = ({
        code,
        phone,
        authHash,
    }: {
        code?: string;
        phone?: string;
        authHash?: string;
    }): Promise<Array<AnyObject>> => {
        return SurveyRequest.post('activate/auth_code', { code, phone, authHash }, {}, { retries: 1 });
    };

    deleteCachedData = async () => {
        await CacheRequest.del(`cache/${tryToGetTtId()}`);
        nextSendIncomplete = null;
        lastTimeout = null;
        lastData = null;
    };

    deleteFile = (id: number): Promise<any> =>
        SurveyRequest.post('delete/file', {
            id,
        });

    disqualSurvey = async (): Promise<void> => {
        const res = await SurveyRequest.post('disqual/response', { timestamp: Date.now() });

        if (res.code === ApiRespCode.REDIRECT) {
            document.body.innerHTML = '';
            setTimeout(() => {
                window.location.href = res.redirectUrl;
            }, 100);
        }
        return res;
    };

    getAllLanguages = (): Promise<any> => {
        return SurveyRequest.get('get/languages');
    };

    getLanguages = (surveyId: number): Promise<any> => {
        return SurveyRequest.get('get/languages_survey', { surveyId });
    };

    getMaxDiffBlocks = async (questionId: number): Promise<MaxDiffBlockModel> => {
        const { blocks } = await SurveyRequest.get(`list/maxdiff_blocks?questionId=${questionId}`);

        return Object.keys(blocks).map((key) => {
            return mapMaxDiffBlock(blocks[key]);
        });
    };

    getNested = async (
        question_id: number,
        level: number,
        parent_id: number,
    ): Promise<{ items: Array<NestedItem> }> => {
        const startTime = Date.now();
        try {
            this.nestedControl[startTime] = true;
            const lang = getSurveyLang();
            const resp = await SurveyRequest.post('show/nested', {
                question_id,
                level,
                parent_id,
                lang: lang ? lang : undefined,
            });
            const { code, items } = resp;
            this.nestedControl[startTime] = false;
            if (code !== ApiRespCode.OK_CODE) {
                throw resp;
            }

            return {
                items: (items as Array<AnyObject>).map(mapNested),
            };
        } catch (e) {
            this.nestedControl[startTime] = false;
            throw e;
        }
    };

    getResidences = async (level: number, parent: number): Promise<Array<ResidenceModel>> => {
        const { rows = [] } = await SurveyRequest.post('list/residence', {
            level,
            parent,
        });

        return prepareResidences(rows, parent);
    };

    getSurveyData = async (survey: SurveyStore): Promise<any> => {
        const savedTtId = tryToGetTtId() ?? '';
        let saved;

        if (survey.info.savingNotComplete && savedTtId && !isPreview) {
            try {
                const getted = await CacheRequest.get(`cache?key=${savedTtId}`);
                saved = JSON.parse(getted.data);
            } catch {}
        }

        if (saved?.token) {
            this.setToken(saved.token);
            setSurveyTokenCookie(saved.token);
        }

        const {
            pages = [],
            questions = [],
            globalLogic = [],
            linkParams = {},
            quotaExhausted = false,
        } = await SurveyRequest.post(`list/page_question${getSurveyLang() ? `?lang=${getSurveyLang()}` : ''}`);

        // Квота на весь опрос (тип 4) приходит в globalLogic и дальше не мапится,
        // но по ней нужен heartbeat (keep-alive брони прохождения + quotaExhausted).
        const hasSurveyQuota = globalLogic.some((l: any) => l.TYPE === LogicType.TYPE_RESPONSE_QUOTA);

        // Закрытые квоты вопросов по данным сервиса: нужны, чтобы применить
        // действие квоты (дисквал/завершение) сразу при выборе ответа, а не
        // ждать claim. Скрытие вариантов из этого списка делает мэппинг
        // (только для ANSWERS_HIDE).
        const closedQuotaLogicIds: Array<number> = [];
        (questions || []).forEach((q: any) => {
            (q.closedQuotas || []).forEach((closed: any) => closedQuotaLogicIds.push(Number(closed.logicId)));
        });

        return {
            pages: mapPages(pages),
            questions: loadSavedAnswers(
                survey.domain,
                mapQuestions(survey, questions)
                    .sort((a, b) =>
                        typeof a.order === 'number' && typeof b.order === 'number' ? a.order - b.order : 0,
                    )
                    .map(({ order: _, ...q }) => ({ ...q })),
                saved?.answers,
            ),
            linkParamsLogic: globalLogic
                .filter((l) => l.TYPE !== LogicType.TYPE_RESPONSE_QUOTA && !l.IS_EXTENDED)
                .map(mapLinkParamsLogic),
            extendedLogic: globalLogic.filter((l) => l.IS_EXTENDED).map(mapQuestionLogic),
            linkParams,
            quotaExhausted: Boolean(quotaExhausted),
            hasSurveyQuota,
            closedQuotaLogicIds,
        };
    };

    // Продление TTL живых броней прохождения. alive=false — сессия протухла
    // (броней больше нет), это сигнал перезапросить квоты при возврате.
    // quotaExhausted=true — по одной из броней квота уже набрана (committed
    // >= limit): прохождение нужно завершить досрочно, ответ не засчитается.
    // closedAnswers — снимок закрытых квот: по нему прячем/возвращаем варианты.
    heartbeatQuotaSession = async (): Promise<{
        alive: boolean;
        quotaExhausted: boolean;
        closedAnswers: Array<{ questionId: number; logicId: number; answerIds: Array<number> }>;
    }> => {
        const resp = await SurveyRequest.post('heartbeat/quota_session');

        return {
            alive: Boolean(resp.alive),
            quotaExhausted: Boolean(resp.quotaExhausted),
            closedAnswers: Array.isArray(resp.closedAnswers)
                ? resp.closedAnswers.map((row: any) => ({
                      questionId: Number(row.questionId),
                      logicId: Number(row.logicId),
                      answerIds: Array.isArray(row.answerIds) ? row.answerIds.map(Number) : [],
                  }))
                : [],
        };
    };

    isAnyNestedInProgress = () => Object.keys(this.nestedControl).some((key) => this.nestedControl[key]);

    // Best-effort освобождение броней: вызывается при уходе со страницы,
    // если прохождение не завершилось (слот вернётся сразу, не по TTL).
    releaseQuotaSession = async (): Promise<void> => {
        await SurveyRequest.post('release/quota_session');
    };

    // То же самое, но с keepalive — единственный способ доставить запрос
    // при закрытии вкладки (sendBeacon не умеет X-Authorization).
    releaseQuotaSessionOnUnload = (): void => {
        SurveyRequest.post('release/quota_session', undefined, {}, { keepalive: true }).catch(() => {});
    };

    saveIncomplete = (answers: Array<unknown>) => {
        if (!nextSendIncomplete) {
            nextSendIncomplete = Date.now() + SEND_INCOMPLETE_INTERVAL;
        }
        if (lastTimeout) {
            window.clearTimeout(lastTimeout);
        }
        const data = { token: SurveyRequest.token, answers };

        lastData = data;
        lastTimeout = window.setTimeout(
            async () => {
                try {
                    const ttId = tryToGetTtId();
                    if (ttId) {
                        await CacheRequest.post('cache', {
                            data: JSON.stringify(data),
                            key: ttId,
                        });
                    }
                } catch (e) {
                    // См. sendLastIncompleteImmediately: флаги сбрасываем всегда,
                    // иначе beforeunload-диалог «залипает» после первой ошибки сети.
                    // Сами ответы при этом остаются в localStorage (new_answers__*).
                    console.error(e);
                } finally {
                    lastTimeout = null;
                    nextSendIncomplete = null;
                    lastData = null;
                }
            },
            nextSendIncomplete === null ? SEND_INCOMPLETE_INTERVAL : nextSendIncomplete - Date.now(),
        );
    };

    sendAllAnswers = async (answers: { question: number; data: Array<any> }): Promise<void> => {
        const lang = getSurveyLang();
        return await SurveyRequest.post('add/all_detail', {
            data: answers,
            lang: lang ? lang : undefined,
            timestamp: Date.now(),
        });
    };

    sendAnswers = async (data: any): Promise<any> => {
        const lang = getSurveyLang();
        return await SurveyRequest.post('add/detail', {
            details: data,
            lang: lang ? lang : undefined,
            timestamp: Date.now(),
        });
    };

    setTestAnswer = async (data: any): Promise<any> => {
        return await SurveyRequest.post('set/test_answer', data);
    };

    sendAudio = async (data: any): Promise<any> => {
        const body = new FormData();

        body.append('upfile', data);

        const resp = await SurveyRequest.post('upload/response/audio', body, {
            'Access-Control-Request-Headers': 'Content-Type,X-Authorization',
            'Access-Control-Request-Method': 'POST',
        });
        const { code } = resp;

        if (code !== ApiRespCode.OK_CODE) {
            throw resp;
        }

        return resp;
    };

    sendOfflineAnswers = async (survey: SurveyStore): Promise<any> => {
        const records = await getOfflineAnswers();
        const alias = getUrlSearchParam('alias') || undefined;
        const status = survey.status || undefined;
        const sent = 0;

        for await (const [uid, { answers, start, stop }] of records) {
            try {
                await SurveyRequest.post('add/responses', {
                    responses: [
                        {
                            response_id: uid,
                            survey: survey.info.id,
                            start_date: start,
                            stop_date: stop,
                            status,
                            alias,
                            source: 'offline',
                            details: answers,
                        },
                    ],
                });

                await updateOfflineStats((prev) => ({
                    ...prev,
                    sent: (prev?.sent || 0) + 1,
                }));

                await deleteOfflineAnswer(uid);
            } catch (e) {
                console.error(e);
            }
        }

        return sent;
    };

    sendPhoneForConfirmation = (params: { phone?: string; authHash?: string }): Promise<Array<AnyObject>> => {
        const searchParams = new URLSearchParams(params);

        return SurveyRequest.get(`get/auth_code?${searchParams.toString()}`);
    };

    setToken = (token: string): void => {
        SurveyRequest.token = token;
    };

    startFigmaTest = async (questionId: number): Promise<any> => {
        return SurveyRequest.get(`start/figma_test?questionId=${questionId}`);
    };

    startSurvey = async (
        rewrite: string,
        token: string,
        params: AnyObject,
        search: string,
        lktoken: string,
    ): Promise<any> => {
        const lang = getSurveyLang();
        return SurveyRequest.post('start/response', {
            rewrite,
            ...params,
            offline: isOfflineMode(),
            query_params: search,
            token: /*saved?.token ||*/ token || undefined,
            lktoken: lktoken || undefined,
            fingerprint: getFingerprint(),
            lang: lang ? lang : undefined,
        });
    };

    stopSurvey = async (): Promise<any> => {
        const { questions = [], ...rest } = await SurveyRequest.post('stop/response', { timestamp: Date.now() });
        if (rest.code === ApiRespCode.REDIRECT) {
            document.body.innerHTML = '';
            setTimeout(() => {
                window.location.href = rest.redirectUrl;
            }, 100);
            return rest;
        }
        return {
            ...rest,
            questions: questions.map(mapQuestion),
        };
    };

    updateTranslationsInfo = async (
        survey: SurveyStore,
        lang: string,
    ): Promise<{
        info: Pick<InfoModel, 'params' | 'design'>;
    }> => {
        const info = await SurveyRequest.get(`get/survey_translation?surveyId=${survey.info.id}&lang=${lang}`);

        return {
            info: mapInfoShorted(info),
        };
    };

    updateTranslationsPagesQuestions = async (
        survey: SurveyStore,
        lang: string,
    ): Promise<{
        pages: Array<PageModel>;
        questions: Array<QuestionModel>;
        linkParamsLogic: Array<QuestionLogicModel>;
        extendedLogic: Array<QuestionLogicModel>;
    }> => {
        const {
            pages = [],
            questions = [],
            globalLogic = [],
        } = await SurveyRequest.post(`list/page_question${lang ? `?lang=${lang}` : ''}`);

        return {
            pages: mapPages(pages),
            questions: loadSavedAnswers(survey.domain, mapQuestions(survey, questions), null),
            linkParamsLogic: globalLogic
                .filter((l) => l.TYPE !== LogicType.TYPE_RESPONSE_QUOTA && !l.IS_EXTENDED)
                .map(mapLinkParamsLogic),
            extendedLogic: globalLogic.filter((l) => l.IS_EXTENDED).map(mapQuestionLogic),
        };
    };

    uploadFiles = async (surveyId: number, files: Array<File>): Promise<Array<AnyObject>> => {
        const newFiles = [];

        for (const f of files) {
            newFiles.push(await uploadFile(surveyId, f));
        }

        return newFiles;
    };

    uploadFilesBatch = async (surveyId: number, files: Array<File>): Promise<Array<AnyObject>> => {
        if (files.length === 0) {
            return [];
        }

        const body = new FormData();

        for (const f of files) {
            body.append('upfile[]', f, f.name);
        }
        body.append('question', String(surveyId));

        const {
            code,
            files: uploadedFiles,
            message,
        } = await SurveyRequest.post('upload/response/file', body, {
            'Access-Control-Request-Headers': 'Content-Type,X-Authorization',
            'Access-Control-Request-Method': 'POST',
        });

        if (code !== ApiRespCode.OK_CODE) {
            const err = new Error(message);
            console.error(err);
            throw err;
        }

        return uploadedFiles.map((file: AnyObject, index: number) => ({
            id: parseInt(String(file.id)),
            type: null,
            file: {
                id: parseInt(String(file.id)),
                path: String(file.path),
                name: String(file.realName),
                size: files[index].size,
            },
        }));
    };
}

export type SavedRecord = QuestionModel & { ts: number };
export type SavedQuestions = Record<number, SavedRecord>;

export const surveyApi = new SurveyAPI();

export const addDetails = ({ comment, data, question }: AnswersRequest): void => {
    const ts = +new Date();
    LocalStorage.append<SavedQuestions>('new_question_details', {
        [question]: {
            ts,
            // @ts-ignore
            question,
            data,
            comment,
        },
    });
    startWaiting();
};

export const clearSentDetails = (before: number): void => {
    LocalStorage.filter<SavedRecord>('new_question_details', (item) => {
        if (item.ts) {
            return item.ts > before;
        }

        return false;
    });
};

export const detailsPending = (): boolean => {
    const queue = LocalStorage.get<SavedQuestions>('new_question_details');
    return queue && Object.keys(queue).length > 0;
};

const SEND_DETAILS_INTERVAL = 10 * 1000;
let sendDetailsInterval = null;

let lastRequest: Promise<any> = null;

// TODO move to service worker
// TODO move timer to survey store
// TODO merge with surveyApi

const startWaiting = (): void => {
    if (!sendDetailsInterval && lastRequest === null) {
        sendDetailsInterval = setTimeout(sendDetails, SEND_DETAILS_INTERVAL);
    }
};

export const stopWaiting = (): void => {
    clearTimeout(sendDetailsInterval);
    sendDetailsInterval = null;
};

// Финализация прохождения (add/all_detail или дисквал): частичный add/detail
// больше не нужен — гасим таймер и чистим очередь. Вызывать нужно до финального
// запроса: пока он в полёте, таймер успевает сработать и отправить add/detail
// ещё раз, хотя все ответы уже уехали в add/all_detail (#1152).
export const cancelPendingDetails = (): void => {
    stopWaiting();
    LocalStorage.remove('new_question_details');
};

export const sendSavedDetails = async (): Promise<any> => {
    stopWaiting();

    let details = LocalStorage.get<SavedQuestions>('new_question_details');
    details = details || {};
    const values: any = Object.values(details);

    if (values.length < 1) return true;

    const data = values.filter((item) => Boolean(item.question && item.data));
    const now = +new Date();
    const resp = await surveyApi.sendAnswers(data);

    clearSentDetails(now);

    return Promise.resolve(resp);
};

// SurveyRequest.post на не-200 реджектит объектом ответа (throw resp), а не Error.
// console.error такого объекта создаёт в Sentry группу "[object Object]" — FRONT-2JD.
// Логируем только настоящие ошибки, о статусе ответа уже сообщает fetch.ts (warning).
const logOnlyError = (e: unknown): void => {
    if (e instanceof Error) {
        console.error(e);
    }
};

const sendDetails = (): void => {
    if (lastRequest !== null) return;

    try {
        lastRequest = sendSavedDetails();
    } catch (e) {
        logOnlyError(e);
    }

    lastRequest.catch(logOnlyError).finally(() => {
        lastRequest = null;
        // startWaiting не работает, пока lastRequest не null, поэтому
        // перезапускаем ожидание здесь, а не в then. Ретраим только если
        // очередь не пуста (т.е. отправка не удалась): иначе detailsPending()
        // навсегда остаётся true, и beforeunload-диалог «залипает».
        if (detailsPending()) {
            startWaiting();
        }
    });
};

const offlineAnswersStore = isOfflineMode() ? createStore('offline-answers-db', 'answers') : null;
const offlineStatStore = isOfflineMode() ? createStore('offline-stat', 'stat') : null;

export const saveAnswersOffline = async (answers: Array<AnswersRequest>, survey: SurveyStore): Promise<any> =>
    Promise.all([
        update(
            nanoid(),
            (_prev) => ({
                answers,
                start: survey.startDatetime.toISOString(),
                stop: survey.stopDatetime.toISOString(),
            }),
            offlineAnswersStore,
        ),
        updateOfflineStats((prev) => ({
            ...prev,
            total: (prev?.total || 0) + 1,
        })),
    ]);

export const getOfflineAnswers = async (): Promise<Array<any>> => await entries(offlineAnswersStore);

export const deleteOfflineAnswer = async (id: string): Promise<void> => await del(id, offlineAnswersStore);

export const getOfflineAnswersCount = async (): Promise<number> => (await keys(offlineAnswersStore)).length;

export interface OfflineStats {
    sent: number;
    total: number;
}

export const getOfflineStats = async (): Promise<OfflineStats> => {
    const stat = await get('stat', offlineStatStore);
    return {
        sent: stat?.sent || 0,
        total: stat?.total || 0,
    };
};

export const updateOfflineStats = async (cb: (prev: OfflineStats) => OfflineStats): Promise<void> =>
    update(
        'stat',
        (prev) => {
            const next = cb(prev);
            return { ...prev, ...next };
        },
        offlineStatStore,
    );

// Пока прохождение активно, у него есть брони квот: при уходе со страницы
// их нужно освободить, иначе слот провисит до TTL. Флаг дублируем в
// sessionStorage (per-tab): после перезагрузки in-memory состояние теряется,
// а release на reload нужен только там, где квота-сессия реально стартовала
// (см. bootstrap в actions.ts).
let quotaSessionActive = false;

const quotaSessionKey = (domain: string): string => `quota_session_active__${domain}`;

export const isQuotaSessionActive = (domain: string): boolean => {
    try {
        return SessionStorage.get<boolean>(quotaSessionKey(domain)) === true;
    } catch {
        // sessionStorage может быть недоступен (приватный режим, sandboxed iframe):
        // считаем, что сессии нет, — release не отправится, слот вернётся по TTL.
        return false;
    }
};

export const setQuotaSessionActive = (domain: string, active: boolean): void => {
    quotaSessionActive = active;
    try {
        if (active) {
            SessionStorage.set(quotaSessionKey(domain), true);
        } else {
            SessionStorage.remove(quotaSessionKey(domain));
        }
    } catch {}
};

// Освобождение броней при уходе. Слушаем pagehide, а не beforeunload:
// pagehide срабатывает и при закрытии вкладки, и при перезагрузке, и его
// нельзя отменить — а release-запрос в beforeunload сам попадает в
// «pending» и вызывал ложный диалог о несохранённых данных. В bfcache
// (persisted) броню не трогаем: респондент может вернуться на эту страницу.
let quotaReleaseSent = false;
const releaseQuotaOnPageHide = (event: PageTransitionEvent): void => {
    if (!quotaSessionActive || quotaReleaseSent || event.persisted) {
        return;
    }
    quotaReleaseSent = true;
    surveyApi.releaseQuotaSessionOnUnload();
};

isBrowser && window.addEventListener('pagehide', releaseQuotaOnPageHide);

const LEAVE_GRACE_PERIOD = 3_000;
let blockedAt = 0;

// TODO apply for only upload requests
isBrowser &&
    window.addEventListener('beforeunload', (event) => {
        if (hasPendingRequests() || detailsPending() || hasPendingIncompleteSurveyRequests()) {
            const now = Date.now();
            if (now - blockedAt < LEAVE_GRACE_PERIOD) {
                return;
            }
            blockedAt = now;
            stopWaiting();
            try {
                // Обе функции async: синхронный try/catch их реджекты не ловит,
                // поэтому catch вешаем на промис. sendLastIncompleteImmediately
                // ошибки обрабатывает внутри и всегда сбрасывает флаги.
                lastRequest = sendSavedDetails().catch(logOnlyError);
                sendLastIncompleteImmediately();
            } catch (e) {
                logOnlyError(e);
            }
            event.preventDefault();
            event.returnValue = 'Данные еще не отправлены. Уйти со страницы?';
        }
    });

isBrowser &&
    window.addEventListener('online', () => {
        if (!isOfflineMode() && detailsPending()) {
            startWaiting();
        }
    });

isBrowser &&
    window.addEventListener('offline', () => {
        stopWaiting();
    });

if (isBrowser && !isOfflineMode() && detailsPending()) {
    startWaiting();
}
